// Friends.js
// Friends screen — web port of FriendsListModal.swift. Pure friend management:
//   • Invite button (share / copy an invite link)
//   • Single search bar (exact username) with inline search / clear / spinner
//   • RESULTS row with ADD / SENT / FRIENDS state
//   • PENDING REQUESTS (accept / decline)
//   • FRIENDS list (tap remove → confirm)
//
// Challenging a friend happens from the Pick Up hub's CHALLENGE FRIEND picker, so there's no
// per-friend challenge control here — matching the app.
//
// Reachable at /battles/pickup/friends.

import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../managers/AuthContext";
import {
  searchUser,
  sendFriendRequest,
  acceptFriendRequest,
  declineFriendRequest,
  removeFriend,
  listenToFriends,
  listenToIncomingRequests,
  listenToSentRequests,
} from "../managers/friendsService";

const APP_THEME = "#d4f53c";
const APP_STORE_URL = "https://apps.apple.com/app/rosterable/id6761739479";

export default function Friends() {
  const { user } = useAuth();
  const uid = user?.uid || null;
  const myUsername = user?.username || null;
  const navigate = useNavigate();

  const [friends, setFriends] = useState([]);
  const [incoming, setIncoming] = useState([]);
  const [sentUIDs, setSentUIDs] = useState(new Set());
  const [justSent, setJustSent] = useState(new Set());

  const [searchText, setSearchText] = useState("");
  const [result, setResult] = useState(null);
  const [searchState, setSearchState] = useState("idle"); // idle | searching | found | none
  const [confirmRemove, setConfirmRemove] = useState(null);
  const [toast, setToast] = useState("");

  const flash = useCallback((m) => {
    setToast(m);
    window.setTimeout(() => setToast(""), 3000);
  }, []);

  useEffect(() => {
    if (!uid) return undefined;
    const a = listenToFriends(uid, setFriends);
    const b = listenToIncomingRequests(uid, setIncoming);
    const c = listenToSentRequests(uid, setSentUIDs);
    return () => { a(); b(); c(); };
  }, [uid]);

  const performSearch = useCallback(async () => {
    const q = searchText.trim();
    if (!q) return;
    setSearchState("searching");
    setResult(null);
    try {
      const r = await searchUser(q, uid);
      if (r) { setResult(r); setSearchState("found"); }
      else setSearchState("none");
    } catch (_) {
      setSearchState("none");
    }
  }, [searchText, uid]);

  const clearSearch = () => {
    setSearchText("");
    setResult(null);
    setSearchState("idle");
  };

  const sendRequest = async (target) => {
    setJustSent((s) => new Set(s).add(target.id));
    try {
      await sendFriendRequest({ fromUID: uid, fromUsername: myUsername, toUID: target.id, toUsername: target.username });
    } catch (e) {
      setJustSent((s) => { const n = new Set(s); n.delete(target.id); return n; });
      flash(e?.message || "Couldn't send request.");
    }
  };
  const accept = async (req) => {
    try { await acceptFriendRequest({ requestID: req.id, fromUID: req.fromUID, fromUsername: req.fromUsername, toUID: uid }); }
    catch (e) { flash(e?.message || "Accept failed."); }
  };
  const decline = async (req) => {
    try { await declineFriendRequest(req.id); } catch (e) { flash(e?.message || "Decline failed."); }
  };
  const doRemove = async (f) => {
    setConfirmRemove(null);
    try { await removeFriend({ userID: uid, friendUID: f.id }); } catch (e) { flash(e?.message || "Remove failed."); }
  };

  const invite = async () => {
    const msg = `${myUsername || "A friend"} wants to add you as a friend on Rosterable! Download and start drafting today.\n${APP_STORE_URL}`;
    if (navigator.share) {
      try { await navigator.share({ text: msg }); } catch (_) { /* cancelled */ }
    } else if (navigator.clipboard) {
      try { await navigator.clipboard.writeText(msg); flash("Invite copied to clipboard"); } catch (_) { flash("Couldn't copy invite"); }
    }
  };

  if (!uid) {
    return (
      <div style={styles.page}>
        <BackBar onBack={() => navigate("/battles/pickup")} />
        <h2 style={styles.listTitle}>FRIENDS LIST</h2>
        <div style={styles.divider} />
        <div style={styles.empty}>
          <p style={styles.emptyTitle}>SIGN IN REQUIRED</p>
          <p style={styles.emptySub}>Friends needs a Rosterable account.</p>
          <button style={styles.inviteBtn} onClick={() => navigate("/login")}>SIGN IN</button>
        </div>
      </div>
    );
  }

  const searching = searchText.trim().length > 0;
  const resultIsFriend = result && friends.some((f) => f.id === result.id);
  const resultSent = result && (sentUIDs.has(result.id) || justSent.has(result.id));

  return (
    <div style={styles.page}>
      <style>{`@keyframes fr-spin { to { transform: rotate(360deg); } }`}</style>
      <BackBar onBack={() => navigate("/battles/pickup")} />
      <h2 style={styles.listTitle}>FRIENDS LIST</h2>
      <div style={styles.divider} />

      {toast && <div style={styles.toast}>{toast}</div>}

      {/* Invite */}
      <button style={styles.inviteBtn} onClick={invite}>⬆ INVITE A FRIEND TO JOIN ROSTERABLE</button>

      {/* Search bar */}
      <div style={styles.searchBar}>
        <input
          style={styles.searchInput}
          placeholder="Search by exact username..."
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && performSearch()}
          autoCapitalize="none"
          autoCorrect="off"
        />
        <span style={styles.searchTrailing}>
          {searchState === "searching" ? (
            <span style={styles.spinner} />
          ) : searchText ? (
            <button style={styles.trailingBtn} onClick={clearSearch} title="Clear">✕</button>
          ) : (
            <button style={{ ...styles.trailingBtn, color: `${APP_THEME}b3` }} onClick={performSearch} title="Search">⌕</button>
          )}
        </span>
      </div>

      {/* Search results */}
      {searching && (
        <>
          {searchState === "none" && <p style={styles.searchMsg}>No players found matching “{searchText.trim()}”.</p>}
          {searchState === "found" && result && (
            <>
              <SectionHeader label="RESULTS" />
              <div style={styles.rows}>
                <div style={styles.row}>
                  <Avatar initial={result.username.charAt(0).toUpperCase()} tone="muted" />
                  <span style={styles.rowName}>{result.username}</span>
                  {resultIsFriend ? (
                    <span style={{ ...styles.pill, color: APP_THEME, background: `${APP_THEME}1a`, borderColor: `${APP_THEME}4d` }}>FRIENDS</span>
                  ) : resultSent ? (
                    <span style={{ ...styles.pill, color: "rgba(255,255,255,0.4)", background: "rgba(255,255,255,0.06)", borderColor: "rgba(255,255,255,0.12)" }}>SENT</span>
                  ) : (
                    <button style={styles.addBtn} onClick={() => sendRequest(result)}>＋ ADD</button>
                  )}
                </div>
              </div>
            </>
          )}
        </>
      )}

      {/* Pending requests */}
      {incoming.length > 0 && (
        <>
          <SectionHeader label="PENDING REQUESTS" />
          <div style={styles.rows}>
            {incoming.map((req) => (
              <div key={req.id} style={{ ...styles.row, background: `${APP_THEME}0a`, borderColor: `${APP_THEME}26` }}>
                <Avatar initial={req.fromUsername.charAt(0).toUpperCase()} tone="muted" />
                <span style={styles.rowName}>{req.fromUsername}</span>
                <button style={styles.circleAccept} onClick={() => accept(req)}>✓</button>
                <button style={styles.circleGhost} onClick={() => decline(req)}>✕</button>
              </div>
            ))}
          </div>
        </>
      )}

      {/* Friends list */}
      {friends.length === 0 ? (
        <div style={styles.empty}>
          <p style={styles.emptyTitle}>NO FRIENDS YET</p>
          <p style={styles.emptySub}>Search for a player above by username and send them a friend request.</p>
        </div>
      ) : (
        <>
          <SectionHeader label="FRIENDS" />
          <div style={styles.rows}>
            {friends.map((f) => (
              <div key={f.id} style={styles.row}>
                <Avatar initial={f.username.charAt(0).toUpperCase()} tone="theme" />
                <span style={styles.rowName}>{f.username}</span>
                {confirmRemove === f.id ? (
                  <>
                    <button style={styles.confirmRemove} onClick={() => doRemove(f)}>REMOVE</button>
                    <button style={styles.circleGhost} onClick={() => setConfirmRemove(null)}>✕</button>
                  </>
                ) : (
                  <button style={styles.removeBtn} onClick={() => setConfirmRemove(f.id)} title="Remove friend">✕</button>
                )}
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

function BackBar({ onBack }) {
  return (
    <div style={styles.backBar}>
      <button style={styles.iconBtn} onClick={onBack}>‹</button>
      <span style={{ flex: 1 }} />
    </div>
  );
}

function Avatar({ initial, tone }) {
  const s =
    tone === "theme"
      ? { background: `${APP_THEME}1a`, borderColor: `${APP_THEME}33`, color: APP_THEME }
      : { background: "rgba(255,255,255,0.08)", borderColor: "rgba(255,255,255,0.15)", color: "rgba(255,255,255,0.6)" };
  return <span style={{ ...styles.avatar, ...s }}>{initial}</span>;
}

function SectionHeader({ label }) {
  return (
    <div style={styles.sectionHeader}>
      <span style={{ ...styles.sectionLine, background: `linear-gradient(to left, ${APP_THEME}73, transparent)` }} />
      <span style={styles.sectionLabel}>{label}</span>
      <span style={{ ...styles.sectionLine, background: `linear-gradient(to right, ${APP_THEME}73, transparent)` }} />
    </div>
  );
}

const styles = {
  page: { padding: "8px 16px 96px", maxWidth: 560, margin: "0 auto", color: "#e8e9ec", minHeight: "100vh" },
  backBar: { display: "flex", alignItems: "center", height: 36 },
  iconBtn: { background: "none", border: "none", color: "rgba(255,255,255,0.8)", fontSize: 28, cursor: "pointer", width: 32, lineHeight: 1 },
  listTitle: { textAlign: "center", fontSize: 16, fontWeight: 600, letterSpacing: 2.5, color: "rgba(255,255,255,0.7)", margin: "8px 0 10px" },
  divider: { height: 1, background: "rgba(255,255,255,0.06)", marginBottom: 14 },
  toast: { background: "#1d2333", border: `1px solid ${APP_THEME}`, color: "#e8e9ec", borderRadius: 10, padding: "10px 12px", marginBottom: 12, fontSize: 13 },
  inviteBtn: { width: "100%", height: 42, borderRadius: 12, border: `1px solid ${APP_THEME}66`, background: APP_THEME, color: "#0c0d10", fontWeight: 800, fontSize: 12, letterSpacing: 0.5, cursor: "pointer", marginBottom: 12 },
  searchBar: { display: "flex", alignItems: "center", gap: 10, padding: 12, borderRadius: 12, background: "rgba(255,255,255,0.06)", border: "1px solid rgba(255,255,255,0.1)", marginBottom: 14 },
  searchInput: { flex: 1, background: "none", border: "none", outline: "none", color: "#fff", fontSize: 14, fontWeight: 500 },
  searchTrailing: { width: 20, height: 20, display: "flex", alignItems: "center", justifyContent: "center" },
  trailingBtn: { background: "none", border: "none", color: "rgba(255,255,255,0.3)", fontSize: 16, cursor: "pointer", lineHeight: 1, padding: 0 },
  spinner: { width: 14, height: 14, borderRadius: 7, border: `2px solid ${APP_THEME}`, borderTopColor: "transparent", display: "inline-block", animation: "fr-spin 0.7s linear infinite" },
  searchMsg: { fontSize: 12, fontWeight: 500, color: "rgba(255,255,255,0.4)", marginBottom: 12 },
  sectionHeader: { display: "flex", alignItems: "center", gap: 10, margin: "4px 0 8px" },
  sectionLine: { flex: 1, height: 1 },
  sectionLabel: { fontSize: 10, fontWeight: 700, letterSpacing: 2, color: APP_THEME, whiteSpace: "nowrap" },
  rows: { display: "flex", flexDirection: "column", gap: 8, marginBottom: 20 },
  row: { display: "flex", alignItems: "center", gap: 12, padding: 12, borderRadius: 14, background: "#16181d", border: "1px solid rgba(255,255,255,0.07)" },
  rowName: { flex: 1, fontSize: 14, fontWeight: 800, color: "#fff", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" },
  avatar: { width: 40, height: 40, borderRadius: 20, border: "1px solid", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 16, fontWeight: 800, flexShrink: 0 },
  pill: { fontSize: 10, fontWeight: 800, letterSpacing: 0.5, padding: "6px 10px", borderRadius: 999, border: "1px solid" },
  addBtn: { display: "flex", alignItems: "center", gap: 4, padding: "7px 12px", borderRadius: 999, border: "none", background: APP_THEME, color: "#0c0d10", fontWeight: 800, fontSize: 11, letterSpacing: 0.5, cursor: "pointer" },
  circleAccept: { width: 34, height: 34, borderRadius: 17, border: "none", background: APP_THEME, color: "#0c0d10", fontWeight: 800, fontSize: 14, cursor: "pointer", flexShrink: 0 },
  circleGhost: { width: 34, height: 34, borderRadius: 17, border: "1px solid rgba(255,255,255,0.12)", background: "rgba(255,255,255,0.08)", color: "rgba(255,255,255,0.5)", fontWeight: 800, fontSize: 13, cursor: "pointer", flexShrink: 0 },
  removeBtn: { width: 34, height: 34, borderRadius: 17, border: "1px solid rgba(255,255,255,0.1)", background: "transparent", color: "rgba(255,255,255,0.3)", fontWeight: 700, fontSize: 13, cursor: "pointer", flexShrink: 0 },
  confirmRemove: { padding: "7px 12px", borderRadius: 999, border: "none", background: "#ff5a5a", color: "#0c0d10", fontWeight: 800, fontSize: 11, letterSpacing: 0.5, cursor: "pointer" },
  empty: { textAlign: "center", padding: "40px 16px" },
  emptyTitle: { fontSize: 13, fontWeight: 800, letterSpacing: 1.5, color: "rgba(255,255,255,0.3)", margin: "0 0 6px" },
  emptySub: { fontSize: 12, fontWeight: 500, color: "rgba(255,255,255,0.22)", lineHeight: 1.5, maxWidth: 320, margin: "0 auto" },
};
